import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { styles } from './CheckboxTreeSelect.styles';

export type ICheckboxTreeNode = {
  id: string;
  displayName: string;
  children?: ICheckboxTreeNode[];
  disabled?: boolean;
  tooltip?: string;
};

export type ICheckboxTreeSelectProps = {
  data: ICheckboxTreeNode[];
  selectedIds: string[];
  onChange(selectedIds: string[]): void;
  isLoading?: boolean;
  showFilter?: boolean;
  filterPlaceholder?: string;
  maxHeight?: number;
};

const collectParentIds = (nodes: ICheckboxTreeNode[]): string[] =>
  nodes.flatMap((node) =>
    node.children?.length ? [node.id, ...collectParentIds(node.children)] : []
  );

const collectLeafIds = (nodes: ICheckboxTreeNode[]): string[] =>
  nodes.flatMap((node) =>
    node.children?.length ? collectLeafIds(node.children) : node.disabled ? [] : [node.id]
  );

const matchesFilter = (node: ICheckboxTreeNode, filter: string): boolean => {
  if (node.displayName.toLowerCase().includes(filter)) return true;
  return (node.children ?? []).some((child) => matchesFilter(child, filter));
};

const filterTree = (nodes: ICheckboxTreeNode[], filter: string, ancestorMatched = false): ICheckboxTreeNode[] => {
  if (!filter) return nodes;
  return nodes
    .filter((node) => ancestorMatched || matchesFilter(node, filter))
    .map((node) => {
      const selfMatches = node.displayName.toLowerCase().includes(filter);
      return { ...node, children: filterTree(node.children ?? [], filter, ancestorMatched || selfMatches) };
    });
};

const getCheckedState = (node: ICheckboxTreeNode, selectedIds: string[]): boolean | 'indeterminate' => {
  if (!node.children?.length) return selectedIds.includes(node.id);
  const leafIds = collectLeafIds([node]);
  const selectedCount = leafIds.filter((id) => selectedIds.includes(id)).length;
  if (selectedCount === 0) return false;
  if (selectedCount === leafIds.length) return true;
  return 'indeterminate';
};

type ITreeNodeProps = {
  node: ICheckboxTreeNode;
  selectedIds: string[];
  expandedIds: Set<string>;
  depth: number;
  onToggleExpand(id: string): void;
  onCheck(node: ICheckboxTreeNode, checked: boolean): void;
};

const TreeNode = ({ node, selectedIds, expandedIds, depth, onToggleExpand, onCheck }: ITreeNodeProps) => {
  const hasChildren = (node.children ?? []).length > 0;
  const isExpanded = expandedIds.has(node.id);
  const checkedState = getCheckedState(node, selectedIds);

  return (
    <>
      <ListItem disablePadding sx={{ paddingLeft: `${depth * 20}px` }} data-test="checkboxTree-node">
        <Box sx={{ ...styles.nodeRow, ...(node.disabled ? styles.nodeRowDisabled : {}) }}>
          {hasChildren ? (
            <IconButton
              size="small"
              role="button"
              sx={styles.expandButton}
              onClick={() => onToggleExpand(node.id)}
              aria-label={isExpanded ? `Collapse ${node.displayName}` : `Expand ${node.displayName}`}
            >
              <SvgIcon
                component={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                inheritViewBox
                sx={styles.toggleIcon}
                aria-hidden={true}
                data-test={isExpanded ? 'checkboxTree-toggle-button-icon-arrow-chevron-down-small' : 'checkboxTree-toggle-button-icon-arrow-chevron-right-small'}
              />
            </IconButton>
          ) : (
            <Box sx={styles.expandPlaceholder} />
          )}
          <FormControlLabel
            sx={{ flex: 1, margin: 0, overflow: 'hidden', ...(node.disabled ? styles.nodeRowDisabledLabel : {}) }}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                <Tooltip title={node.displayName} placement="bottom" enterDelay={500}>
                  <Typography sx={styles.nodeLabel}>{node.displayName}</Typography>
                </Tooltip>
                {node.disabled && node.tooltip && (
                  <Typography sx={styles.nodeLabelDisabled}>{node.tooltip}</Typography>
                )}
              </Box>
            }
            control={
              <Checkbox
                size="small"
                checked={checkedState === true}
                indeterminate={checkedState === 'indeterminate'}
                disabled={node.disabled}
                onChange={(_e, checked) => onCheck(node, checked)}
                inputProps={{ 'aria-label': node.displayName, className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          />
        </Box>
      </ListItem>
      {hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          <List disablePadding>
            {(node.children ?? []).map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                selectedIds={selectedIds}
                expandedIds={expandedIds}
                depth={depth + 1}
                onToggleExpand={onToggleExpand}
                onCheck={onCheck}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const CheckboxTreeSelect = ({
  data,
  selectedIds,
  onChange,
  isLoading = false,
  showFilter = true,
  filterPlaceholder = 'Search...',
  maxHeight = 150,
}: ICheckboxTreeSelectProps) => {
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const savedExpandedIds = useRef<Set<string>>(new Set());
  const dataInitialized = useRef(false);
  const prevFilter = useRef('');

  const filteredData = useMemo(() => filterTree(data, filter.toLowerCase()), [data, filter]);

  useEffect(() => {
    if (!dataInitialized.current && data.length > 0) {
      dataInitialized.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (filter) {
      if (!prevFilter.current) {
        savedExpandedIds.current = expandedIds;
      }
      setExpandedIds(new Set(collectParentIds(filteredData)));
    } else {
      setExpandedIds(savedExpandedIds.current);
    }
    prevFilter.current = filter;
  }, [filter]);

  const allLeafIds = useMemo(() => collectLeafIds(data), [data]);
  const allSelected = allLeafIds.length > 0 && allLeafIds.every((id) => selectedIds.includes(id));
  const someSelected = !allSelected && allLeafIds.some((id) => selectedIds.includes(id));

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCheck = (node: ICheckboxTreeNode, checked: boolean) => {
    const leafIds = collectLeafIds([node]);
    if (!leafIds.length && !node.children?.length) {
      // leaf node
      const next = checked
        ? [...selectedIds, node.id]
        : selectedIds.filter((id) => id !== node.id);
      onChange(next);
    } else {
      // parent node — toggle all enabled leaves
      const next = checked
        ? [...new Set([...selectedIds, ...leafIds])]
        : selectedIds.filter((id) => !leafIds.includes(id));
      onChange(next);
    }
  };

  const handleSelectAll = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange(checked ? allLeafIds : []);
  };

  return (
    <Box sx={styles.root}>
      {showFilter && (
        <Box sx={styles.filterBox}>
          <TextField
            size="small"
            fullWidth
            placeholder={filterPlaceholder}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            inputProps={{ 'aria-label': filterPlaceholder, className: 'no-format' }}
          />
        </Box>
      )}
      <Box sx={styles.selectAllRow}>
        <FormControlLabel
          label={<Typography variant="body2">Select All</Typography>}
          control={
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              inputProps={{ 'aria-label': 'Select all', className: 'no-format', 'data-test': 'checkboxTree-select-all' } as React.InputHTMLAttributes<HTMLInputElement>}
            />
          }
        />
      </Box>
      <Box sx={{ ...styles.treeBox, maxHeight: `${maxHeight}px` }}>
        {isLoading ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List disablePadding>
            {filteredData.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                selectedIds={selectedIds}
                expandedIds={expandedIds}
                depth={0}
                onToggleExpand={handleToggleExpand}
                onCheck={handleCheck}
              />
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};
